<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-5xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-4">
        <div>
          <h1 class="sci-page-title-only">Tipos de Vínculos</h1>
          <p class="text-sm text-slate-500">Gerencie as categorias de vínculos do sistema</p>
        </div>
      </div>

      <!-- FORMULÁRIO DE CADASTRO -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-link-variant</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                  {{ bondData.bond_id ? 'EDITAR VÍNCULO' : 'NOVO VÍNCULO' }}
              </h2>
          </div>
          
          <div class="p-8">
              <v-form @submit.prevent="createOrUpdate">
                <div class="flex flex-col md:flex-row md:items-center gap-3">
                  <div class="flex-1 min-w-0">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Nome do Vínculo</label>
                    <v-text-field
                      v-model="bondData.bond_desc"
                      ref="bondName"
                      placeholder="Ex: Efetivo, Comissionado..."
                      outlined
                      dense
                      hide-details
                      class="custom-input"
                    />
                  </div>
                  
                  <div class="flex gap-2 flex-none pt-6">
                    <button 
                      type="submit"
                      class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[10px] px-4 py-2.5 rounded shadow transition-all uppercase flex items-center justify-center gap-2 whitespace-nowrap h-[40px]"
                    >
                      <v-icon small color="white">mdi-check</v-icon>
                      {{ bondData.bond_id ? 'EDITAR' : 'SALVAR' }}
                    </button>
                    <button 
                      v-if="bondData.bond_id"
                      type="button"
                      @click="newData"
                      class="bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-[10px] px-4 py-2.5 rounded transition-all uppercase whitespace-nowrap h-[40px]"
                    >
                      LIMPAR
                    </button>
                  </div>
                </div>
                <p v-if="bondData.bond_id" class="mt-4 text-[10px] text-orange-500 font-medium italic">
                  * Você está editando um item. Para adicionar um novo, clique em "LIMPAR".
                </p>
              </v-form>
          </div>
      </section>

      <!-- GRID DE DADOS -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
              <div class="flex items-center gap-3">
                <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">VÍNCULOS CADASTRADOS</h2>
              </div>
              
              <div class="flex items-center gap-4 flex-1 justify-end">
                <div class="relative w-80">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <v-icon color="white" small class="opacity-50">mdi-magnify</v-icon>
                  </div>
                  <input 
                    v-model="search"
                    placeholder="PESQUISAR..."
                    class="bg-white/10 border border-white/20 rounded-lg py-2 pl-10 pr-4 text-[11px] text-white placeholder:text-white/40 focus:outline-none focus:bg-white/20 transition-all w-full uppercase tracking-widest"
                  >
                </div>
              </div>
          </div>

          <div class="p-0">
              <v-data-table
                :headers="headers"
                :items="data"
                :search="search"
                :items-per-page="10"
                :loading="loader"
                loading-text="CARREGANDO..."
                class="custom-table"
                hide-default-footer
              >
                <template v-slot:item.bond_desc="{ item }">
                  <span class="text-sm font-medium text-slate-700 uppercase">{{ item.bond_desc }}</span>
                </template>

                <template v-slot:item.action="{ item }">
                  <div class="flex justify-end gap-2">
                    <button 
                      @click="loadData(item)"
                      class="p-2 hover:bg-orange-50 text-orange-600 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <v-icon small color="orange">mdi-pencil</v-icon>
                    </button>
                    <button 
                      @click="deleteItem(item.bond_id)"
                      class="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                      title="Excluir"
                    >
                      <v-icon small color="red">mdi-delete</v-icon>
                    </button>
                  </div>
                </template>

                <template v-slot:no-data>
                  <div class="py-12 flex flex-col items-center justify-center text-slate-400">
                    <v-icon size="48" color="slate-200">mdi-database-off</v-icon>
                    <p class="mt-2 text-sm">Nenhum vínculo encontrado</p>
                  </div>
                </template>
              </v-data-table>

              <div class="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <span class="text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                  Total: {{ data.length }} registros
                </span>
              </div>
          </div>
      </section>
    </div>

    <!-- NOTIFICAÇÕES -->
    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      timeout="3000"
      rounded="lg"
      elevation="24"
    >
      <div class="flex items-center gap-3">
        <v-icon color="white" small>
          {{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
        </v-icon>
        <span class="text-xs font-bold uppercase tracking-wider">{{ snack.message }}</span>
      </div>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  props: ["id"],
  data() {
    return {
      search: "",
      loader: true,
      data: [],
      bondData: {
        bond_desc: "",
        bond_id: null
      },
      snack: {
        show: false,
        message: "",
        color: ""
      },
      headers: [
        {
          text: "Nome",
          align: "start",
          sortable: false,
          value: "bond_desc"
        },
        { text: "Ações", align: "right", value: "action", sortable: false }
      ]
    };
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      this.data = [];
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(`/settings/bonds`)
        .then(r => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch(e => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    newData() {
      this.bondData.bond_desc = "";
      this.bondData.bond_id = null;
      this.$refs.bondName.focus();
    },
    loadData(data) {
      this.bondData.bond_desc = data.bond_desc;
      this.bondData.bond_id = data.bond_id;
    },
    createOrUpdate() {
      if (this.bondData.bond_id) {
        this.$http
          .put(`/settings/bonds/${this.bondData.bond_id}`, this.bondData)
          .then(r => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = true;
            this.initialize();
            this.$store.dispatch("setLoader", this.loader);
            this.newData();
          })
          .catch(e => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      } else {
        this.$http
          .post(`/settings/bonds`, this.bondData)
          .then(r => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = true;
            this.initialize();
            this.$store.dispatch("setLoader", this.loader);
            this.newData();
          })
          .catch(e => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    deleteItem(id) {
      let c = confirm("Deseja excluir o registro?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/settings/bonds/${id}`)
          .then(r => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.initialize();
          })
          .catch(e => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    }
  }
};
</script>

<style scoped>
.custom-table ::v-deep th {
  background-color: #f8fafc !important;
  text-transform: uppercase !important;
  font-size: 10px !important;
  font-weight: 700 !important;
  color: #64748b !important;
  letter-spacing: 0.05em !important;
  height: 48px !important;
}

.custom-table ::v-deep td {
  height: 56px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.custom-table ::v-deep tr:hover {
  background-color: #f8fafc !important;
}

.custom-input ::v-deep .v-input__slot {
  background-color: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  box-shadow: none !important;
}

.custom-input ::v-deep .v-input__slot:hover {
  border-color: #cbd5e1 !important;
}

.custom-input ::v-deep fieldset {
  border: none !important;
}

.custom-input ::v-deep input {
  font-size: 13px !important;
  color: #334155 !important;
}
</style>